/**
 * Уведомления оператора: звук, системное уведомление, счётчик в заголовке.
 *
 * Перенесено из старой панели вместе с главным правилом: НЕ уведомлять о
 * диалоге, который сейчас открыт и виден. Иначе оператор получает пинг на
 * каждое сообщение в переписке, которую сам же и читает, — и через полчаса
 * выключает звук совсем.
 */

const BASE_TITLE = 'Поддержка';

// ──────────────────────────── Звук ────────────────────────────

/**
 * Тон генерируется WebAudio, а не грузится файлом.
 *
 * Причина прозаическая: файл — это ещё один сетевой запрос, который в момент
 * пинга может не успеть, и первое уведомление за сессию оказывается немым.
 * Параметры (880 Гц, 0.28 с) взяты из старой панели без изменений — операторы
 * этот звук узнают.
 */
export function playPing(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = 880;
    // Экспонента, а не резкий старт: щелчок на прямоугольном фронте слышен
    // лучше самого тона.
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);

    osc.start();
    osc.stop(ctx.currentTime + 0.28);
    // Контексты не бесплатны: браузер держит их пул и на сотом откажет.
    window.setTimeout(() => void ctx.close(), 400);
  } catch {
    // Автовоспроизведение заблокировано до первого клика — это норма, а не сбой.
  }
}

// ──────────────────────────── Системные уведомления ────────────────────────────

export function canNotify(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Спросить разрешение.
 *
 * Только из обработчика жеста пользователя: Chrome игнорирует запрос, сделанный
 * при загрузке страницы, и второго шанса спросить уже не даёт.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

export interface NotifyInput {
  /** Диалог, о котором уведомляем: он же tag, чтобы пинги не копились стопкой. */
  conversationId: string;
  title: string;
  body: string;
  /** Кликнули по уведомлению — открыть диалог. */
  onClick?: () => void;
}

/** Что сейчас открыто у оператора. По ней решается, молчать или нет. */
let activeConversationId: string | null = null;

export function setActiveConversation(conversationId: string | null): void {
  activeConversationId = conversationId;
}

/** Звук включён. Настройка живёт в сторе ui и переносится сюда одной строкой. */
let soundEnabled = true;

export function setSoundEnabled(enabled: boolean): void {
  soundEnabled = enabled;
}

/**
 * Уведомить о входящем.
 *
 * Молчим в единственном случае: вкладка на экране И открыт именно этот диалог.
 * Свёрнутая вкладка уведомление получает всегда — иначе смысл теряется.
 */
export function notifyIncoming(input: NotifyInput): void {
  const visible = document.visibilityState === 'visible';
  if (visible && activeConversationId === input.conversationId) return;

  if (soundEnabled) playPing();
  if (!canNotify()) return;

  try {
    const notification = new Notification(input.title, {
      // 140 символов: длиннее ни одна платформа всё равно не покажет.
      body: input.body.slice(0, 140),
      // tag по диалогу: десять сообщений подряд схлопываются в одно
      // уведомление, а не строят башню на весь экран.
      tag: `conv:${input.conversationId}`,
      renotify: true,
    } as NotificationOptions);

    notification.onclick = () => {
      window.focus();
      notification.close();
      input.onClick?.();
    };
  } catch {
    // Safari бросает на renotify без ServiceWorker — звук уже отыграл.
  }
}

// ──────────────────────────── Счётчик во вкладке ────────────────────────────

let titleSuffix = BASE_TITLE;

/** Раздел панели в заголовке: «(3) Чат · Поддержка». */
export function setTitleSection(section: string | null): void {
  titleSuffix = section ? `${section} · ${BASE_TITLE}` : BASE_TITLE;
  applyTitle();
}

let unreadTotal = 0;

/**
 * Общее число непрочитанных в заголовке вкладки.
 *
 * Заголовок — единственное место, которое оператор видит из другой вкладки:
 * системные уведомления он мог запретить, звук — выключить.
 */
export function setUnreadTotal(total: number): void {
  const next = Math.max(0, Math.trunc(total));
  if (next === unreadTotal) return;
  unreadTotal = next;
  applyTitle();
}

function applyTitle(): void {
  document.title = unreadTotal > 0 ? `(${unreadTotal}) ${titleSuffix}` : titleSuffix;
}
